import { useState, useEffect } from 'react';
import { Download, X, Share, PlusSquare, Smartphone } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

const DISMISS_KEY = 'pesajet_pwa_install_dismissed_at';
const SNOOZE_DAYS = 7;

export default function PWAInstallPrompt() {
  const { isInstallable, isInstalled, isIOS, isStandalone, installApp } = usePWA();
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isInstalled || isStandalone) {
      setVisible(false);
      return;
    }

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const diffMs = Date.now() - Number(dismissedAt);
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      if (diffDays < SNOOZE_DAYS) {
        return;
      }
    }

    if (isInstallable) {
      // Delay prompt slightly for a smooth entrance
      const timer = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled, isStandalone]);

  if (!visible || isInstalled || isStandalone) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
  };

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    setInstalling(true);
    const installed = await installApp();
    setInstalling(false);
    if (installed) {
      setVisible(false);
    }
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
      <div className="bg-slate-900/95 dark:bg-slate-900/95 backdrop-blur-md text-white p-4 sm:p-5 rounded-3xl shadow-2xl border border-slate-800 flex flex-col gap-3.5">
        {/* Header with icon & close */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-purple-500/15 border border-purple-500/25 flex items-center justify-center text-purple-400 shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-100 flex items-center gap-1.5">
                Install PesaJet Pay
                <span className="text-[10px] font-semibold uppercase tracking-wider bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded-full">
                  App
                </span>
              </h4>
              <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                Fast access to collections, payouts and ledger right from your home screen.
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Close install prompt"
            className="text-slate-400 hover:text-slate-200 p-1 rounded-xl hover:bg-slate-800 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* iOS Instruction Modal or Dropdown */}
        {showIOSGuide ? (
          <div className="p-3 bg-slate-800/80 rounded-2xl border border-slate-700 text-xs space-y-2 animate-in fade-in">
            <p className="font-semibold text-purple-400 flex items-center gap-1.5">
              <Share className="w-3.5 h-3.5" /> How to install on iOS Safari:
            </p>
            <ol className="list-decimal list-inside space-y-1 text-slate-300 text-[11px] leading-relaxed">
              <li>
                Tap the <span className="font-bold text-white">Share</span> button in Safari's
                toolbar.
              </li>
              <li>
                Scroll down and select{' '}
                <span className="font-bold text-white flex-inline items-center gap-1">
                  <PlusSquare className="w-3 h-3 inline mr-0.5" />
                  Add to Home Screen
                </span>
                .
              </li>
              <li>
                Tap <span className="font-bold text-white">Add</span> in the top right corner.
              </li>
            </ol>
            <div className="flex justify-end pt-1">
              <button
                onClick={() => setShowIOSGuide(false)}
                className="text-[11px] text-purple-400 hover:text-purple-300 font-bold"
              >
                Got it
              </button>
            </div>
          </div>
        ) : (
          /* Action Buttons */
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-xl transition"
            >
              Maybe Later
            </button>
            <button
              onClick={handleInstallClick}
              disabled={installing}
              className="px-4 py-2 bg-gradient-to-r from-purple-700 to-purple-600 hover:from-purple-600 hover:to-purple-500 active:from-purple-800 active:to-purple-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-lg shadow-purple-950/40 transition disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" />
              {isIOS ? 'Install Instructions' : installing ? 'Installing...' : 'Install App'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
